import React, {useEffect, useState} from 'react';
import '../index.css';
import {
    AppstoreOutlined,
    EditOutlined,
    PieChartOutlined,
    PlayCircleOutlined,
    ExperimentOutlined,
    AimOutlined,
    ProfileOutlined,
    LockOutlined,
    CodeOutlined,
    ToolOutlined,
    ClusterOutlined, ClockCircleOutlined, SaveOutlined
} from '@ant-design/icons';
import {Layout, Menu, Modal, theme} from 'antd';
import {Route, Routes, useNavigate} from "react-router-dom";
import {useKeycloak} from "@react-keycloak/web";

import CreateTest from "./CreateTest";
import TestOverview from "./TestOverview";
import PrefabOverview from "./PrefabOverview";
import RequestOverview from "./RequestOverview";
import JsonViewer from "./JsonViewer";
import TestPlanOverview from "./TestPlanOverview";
import LocatorOverview from "./LocatorOverview";
import PasswordOverview from "./PasswordOverview";
import TestExecutionOverview from "./TestExecutionOverview";
import Dashboard from "./Dashboard";
import Navbar from "../components/misc/Navbar";
import {hasRole, ROLES} from "../components/misc/RolesHelper";
import {Content} from "antd/es/layout/layout";
import SystemOverview from "./SystemOverview";
import SchedulingOverview from "./SchedulingOverview";
import {useDirty} from "../utils/hook/DirtyProvider";
import ExportImportOverview from "./ExportImportOverview";
import {useSystemContext} from "../utils/hook/SystemProvider";
import PlaywrightConfigurationOverview from "./PlaywrightConfigurationOverview";
import {useKeycloakHeader} from "../config/Config";
import {MgmtInfoControllerApi} from "../api";

const {Header, Footer, Sider} = Layout;

const allRolesAccess = [ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_MANAGER, ROLES.GLOW_TEST_DEVELOPER, ROLES.GLOW_TEST_DESIGNER];

const CustomLayout = () => {
    const navigate = useNavigate();
    const {isDirty, setIsDirty} = useDirty();
    const [collapsed, setCollapsed] = useState(false);
    const [buildInfo, setBuildInfo] = useState<string>();

    const {systems, triggerRefresh} = useSystemContext();

    const {token: {colorBgContainer}} = theme.useToken();
    const {keycloak} = useKeycloak();
    const [selectedKey, setSelectedKey] = useState(process.env.PUBLIC_URL + '/dashboard');
    const headers = useKeycloakHeader();

    const controller = new MgmtInfoControllerApi();

    useEffect(() => {
        fetchBuildInfo();
    });

    const fetchBuildInfo = () => {
        console.log("Fetching build info");
        controller.getBuildTimestamp(headers).then(response => {
            setBuildInfo(response.data);
        }).catch(error => {
            console.error("Error fetching build info", error);
        });
    }


    const handleNavigation = ({key}: { key: string }) => {
        if (isDirty) {
            Modal.confirm({
                title: 'Ungespeicherte Änderungen',
                content: 'Es gibt ungespeicherte Änderungen. Willst du die Seite wirklich verlassen?',
                okText: 'Ja, verlassen',
                cancelText: 'Abbrechen',
                onOk: () => {
                    setIsDirty(false); // Reset isDirty
                    setSelectedKey(key); // Update des ausgewählten Menüelements
                    navigate(key); // Navigation durchführen
                },
                onCancel: () => {
                    // Highlighting bleibt auf dem alten Element, wenn der Benutzer abbricht
                },
            });
            return; // Kein weiterer Code ausführen, Modal übernimmt die Kontrolle
        }

        setIsDirty(false); // Reset isDirty
        setSelectedKey(key); // Update des ausgewählten Menüelements
        navigate(key); // Navigation durchführen
    };

    // Funktion zur Filterung von Menüelementen basierend auf Berechtigungen
    const filteredItems = (menuItems) => {
        return menuItems.filter(item => {
            if (item.permissions.length === 0) return true;
            if (item.children) {
                item.children = item.children.filter(child => hasRole(child.permissions, keycloak));
                return item.children.length > 0;
            } else {
                return hasRole(item.permissions, keycloak);
            }
        });
    };


    // Menüstruktur dynamisch erstellen

    const items = [
        {
            key: process.env.PUBLIC_URL + '/dashboard',
            icon: <PieChartOutlined/>,
            label: 'Dashboard',
            permissions: []
        },
        {
            key: process.env.PUBLIC_URL + '/create-test',
            icon: <EditOutlined/>,
            label: 'Test-Erstellung',
            permissions: allRolesAccess
        },
        {
            key: 'sub1',
            icon: <ExperimentOutlined/>,
            label: 'Tests',
            permissions: allRolesAccess,
            children: Array.isArray(systems) ? systems
                .map(system => ({
                    key: process.env.PUBLIC_URL + `/test/system/${system.name ? system.name.toLowerCase() : "404"}`,
                    label: system.name,
                    permissions: allRolesAccess
                })) : []
        },
        {
            key: 'sub2',
            icon: <AppstoreOutlined/>,
            label: 'Komponenten',
            permissions: allRolesAccess,
            children: Array.isArray(systems) ? systems.map(system => ({
                key: process.env.PUBLIC_URL + `/prefab/system/${system.name ? system.name.toLowerCase() : "404"}`,
                label: system.name,
                permissions: allRolesAccess
            })) : []
        },
        /*
        {
            key: 'sub4',
            icon: <AimOutlined />,
            label: 'Locators',
            permissions: allRolesAccess,
            children: Array.isArray(systems) ? systems.map(system => ({
                key: process.env.PUBLIC_URL + `/locator/system/${system.name ? system.name.toLowerCase() : "404"}`,
                label: system.name,
                permissions: allRolesAccess
            })) : []
        },*/
        {
            key: process.env.PUBLIC_URL + '/test/executions',
            icon: <PlayCircleOutlined/>,
            label: 'Testausführungen',
            permissions: allRolesAccess
        },
        {
            key: process.env.PUBLIC_URL + '/testplans',
            icon: <ProfileOutlined/>,
            label: 'Testpläne',
            permissions: allRolesAccess
        },
        {
            key: process.env.PUBLIC_URL + '/scheduling',
            icon: <ClockCircleOutlined/>,
            label: 'Zeitplanung',
            permissions: allRolesAccess
        },
        {
            key: process.env.PUBLIC_URL + '/passwords',
            icon: <LockOutlined/>,
            label: 'Passwörter',
            permissions: allRolesAccess
        },

        /*{
            key: 'sub3',
            icon: <ApiOutlined />,
            label: 'Requests',
            permissions: allRolesAccess,
            children: systems.map(system => ({
                key: process.env.PUBLIC_URL + `/request/system/${system.name ? system.name.toLowerCase() : "404"}`,
                label: system.name,
                permissions: allRolesAccess
            }))
        },*/
        {
            key: process.env.PUBLIC_URL + '/systems',
            icon: <ClusterOutlined/>,
            label: 'Systeme',
            permissions: [ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_MANAGER]
        },
        {
            key: 'sub5',
            icon: <ToolOutlined/>,
            label: 'Werkzeuge',
            permissions: allRolesAccess,
            children: [
                {
                    key: process.env.PUBLIC_URL + '/json-viewer',
                    icon: <CodeOutlined/>,
                    label: 'Json-Pfad',
                    permissions: allRolesAccess
                },
                {
                    key: process.env.PUBLIC_URL + '/export-import',
                    icon: <SaveOutlined/>,
                    label: 'Sicherung',
                    permissions: allRolesAccess
                },
            ]
        },
        {
            key: process.env.PUBLIC_URL + '/configuration',
            icon: <ClusterOutlined/>,
            label: 'Konfiguration',
            permissions: [ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_MANAGER]
        },
    ];

    // @ts-ignore
    return (
        <Layout style={{minHeight: '100vh'}}>
            <Sider collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)}>
                <Menu
                    theme="dark"
                    defaultSelectedKeys={['1']}
                    mode="inline"
                    items={filteredItems(items)}
                    onClick={handleNavigation}
                    selectedKeys={[selectedKey]}
                />
            </Sider>
            <Layout>
                <Header style={{padding: 0, background: colorBgContainer}}>
                    <Navbar/>
                </Header>
                <Content style={{paddingLeft: 10, paddingRight: 10}}>
                    <Routes>
                        <Route path={process.env.PUBLIC_URL + "/"} element={<Dashboard/>}/>
                        <Route path={process.env.PUBLIC_URL + "/dashboard"} element={<Dashboard/>}/>
                        <Route path={process.env.PUBLIC_URL + "/create-test"} element={<CreateTest/>}/>
                        <Route path={process.env.PUBLIC_URL + "/edit/test/:id"} element={<CreateTest isTest={true}/>}/>
                        <Route path={process.env.PUBLIC_URL + "/edit/prefab/:id"} element={<CreateTest isTest={false}/>}/>
                        {Array.isArray(systems) && systems.map(system => (
                            <React.Fragment key={system.id}>
                                <Route
                                    path={`${process.env.PUBLIC_URL}/test/system/${system.name ? system.name.toLowerCase() : "404"}`}
                                    element={<TestOverview system={system.name}/>}/>
                                <Route
                                    path={`${process.env.PUBLIC_URL}/prefab/system/${system.name ? system.name.toLowerCase() : "404"}`}
                                    element={<PrefabOverview system={system.name}/>}/>
                                <Route
                                    path={`${process.env.PUBLIC_URL}/request/system/${system.name ? system.name.toLowerCase() : "404"}`}
                                    element={<RequestOverview system={system.name}/>}/>
                                <Route
                                    path={`${process.env.PUBLIC_URL}/locator/system/${system.name ? system.name.toLowerCase() : "404"}`}
                                    element={<LocatorOverview system={system.name}/>}/>
                            </React.Fragment>
                        ))}
                        <Route path={process.env.PUBLIC_URL + "/json-viewer"} element={<JsonViewer/>}/>
                        <Route path={process.env.PUBLIC_URL + "/testplans"} element={<TestPlanOverview/>}/>
                        <Route path={process.env.PUBLIC_URL + "/scheduling"} element={<SchedulingOverview/>}/>
                        <Route path={process.env.PUBLIC_URL + "/passwords"} element={<PasswordOverview/>}/>
                        <Route path={process.env.PUBLIC_URL + "/test/executions"} element={<TestExecutionOverview/>}/>
                        <Route path={process.env.PUBLIC_URL + "/systems"} element={<SystemOverview/>}/>
                        <Route path={process.env.PUBLIC_URL + "/export-import"} element={<ExportImportOverview/>}/>
                        <Route path={process.env.PUBLIC_URL + "/configuration"} element={<PlaywrightConfigurationOverview/>}/>
                    </Routes>
                </Content>
                <Footer style={{textAlign: 'center'}}>GLOW - BTC-AG
                    ©2025 {buildInfo && `(Build: ${buildInfo})`}</Footer>
            </Layout>
        </Layout>
    );
};

export default CustomLayout;
